import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getLenis } from "@/components/site/SmoothScroll";

/**
 * Reset scroll to the top of the page on route change.
 *
 * Behaviour:
 *  - Same pathname (only hash / search changed): do nothing — the anchor
 *    click handler in SmoothScroll already scrolls to the target section.
 *  - Different pathname WITH a hash (e.g. "/#contact"): scroll to that
 *    element (needed when the link came from another route entirely).
 *  - Different pathname, no hash: jump to the top of the document. We call
 *    Lenis first (it owns scroll state when smooth-scroll is active) and
 *    then window.scrollTo as a fallback for touch devices where Lenis is
 *    disabled.
 *
 * Rendered inside <BrowserRouter> in App.js. Renders nothing.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // If a hash target exists, defer to the anchor logic — scroll to that
    // element instead of resetting to the top.
    if (hash) {
      // Small delay to let the destination page mount its DOM.
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          const lenis = getLenis();
          if (lenis) lenis.scrollTo(el, { offset: -80, duration: 1.2 });
          else el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 60);
      return () => clearTimeout(t);
    }

    // Otherwise, snap to the top.
    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(0, { immediate: true, force: true });
    }
    // Fallback + safety net for browsers / touch where Lenis is off.
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    if (typeof document !== "undefined") {
      // Some browsers keep the doc scroll separate; nudge both.
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [pathname, hash]);

  return null;
}
